import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  ReferralWorkflowState,
  UseReferralWorkflowReturn,
  PatientData,
  DietitianProfile,
  FilterState,
  ValidationErrors,
} from '../types/referral';
import { useReferralPersistence } from './useReferralPersistence';
import { useReferralDocuments } from './useReferralDocuments';
import { validateEmail } from '../utils/validation/emailValidation';
import { referralDomain } from '../services/domains/ReferralDomain';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Main hook for managing the Provider Referral Workflow state and actions
 * Follows BiteWise patterns for state management and validation
 */
export const useReferralWorkflow = (): UseReferralWorkflowReturn => {
  const logger = getLogger();
  const { saveToStorage, loadFromStorage, clearStorage } = useReferralPersistence();

  const { uploadDocument } = useReferralDocuments();

  // Initialize state with default values
  const [state, setState] = useState<ReferralWorkflowState>(() => {
    const savedState = loadFromStorage();
    return {
      currentStep: savedState?.currentStep || 1,
      patientData: savedState?.patientData || {},
      selectedDietitian: savedState?.selectedDietitian || null,
      availableDietitians: savedState?.availableDietitians || [],
      filters: savedState?.filters || {
        search: '',
        specialty: '',
        location: '',
      },
      isLoading: false,
      errors: savedState?.errors || {},
      isSubmitting: false,
      submissionResult: null,
    };
  });

  // Auto-save state changes to localStorage
  useEffect(() => {
    try {
      saveToStorage(state);
    } catch (error) {
      logger.warn('Failed to save referral workflow state:', error);
    }
  }, [state, saveToStorage]);

  // Validation functions
  const validatePatientData = useCallback((data: Partial<PatientData>): ValidationErrors => {
    const errors: ValidationErrors = {};

    // Patient name validation
    if (!data.patientName?.trim()) {
      errors.patientName = 'Patient name is required';
    } else if (data.patientName.trim().length < 2) {
      errors.patientName = 'Patient name must be at least 2 characters';
    } else if (data.patientName.trim().length > 100) {
      errors.patientName = 'Patient name must be less than 100 characters';
    }

    // Email validation using utility
    if (!data.email?.trim()) {
      errors.email = 'Email is required';
    } else {
      try {
        const emailValidation = validateEmail(data.email);
        if (!emailValidation.isValid) {
          errors.email = emailValidation.error;
        }
      } catch (error) {
        logger.warn('Email validation utility error:', error);
        errors.email = 'Email validation failed';
      }
    }

    // Health conditions validation
    if (!data.conditions || data.conditions.length === 0) {
      errors.conditions = 'Please select at least one health condition';
    }

    return errors;
  }, []);

  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 1: {
        const errors = validatePatientData(state.patientData);
        return Object.keys(errors).length === 0;
      }
      case 2: {
        return state.selectedDietitian !== null;
      }
      case 3: {
        const patientErrors = validatePatientData(state.patientData);
        return Object.keys(patientErrors).length === 0 && state.selectedDietitian !== null;
      }
      default:
        return false;
    }
  }, [state.patientData, state.selectedDietitian, validatePatientData]);

  const getStepErrors = useCallback((step: number): string[] => {
    const errors: string[] = [];

    switch (step) {
      case 1: {
        const validationErrors = validatePatientData(state.patientData);
        errors.push(...Object.values(validationErrors));
        break;
      }
      case 2: {
        if (!state.selectedDietitian) {
          errors.push('Please select a dietitian');
        }
        break;
      }
      case 3: {
        const patientErrors = validatePatientData(state.patientData);
        errors.push(...Object.values(patientErrors));
        if (!state.selectedDietitian) {
          errors.push('Please select a dietitian');
        }
        break;
      }
    }

    return errors;
  }, [state.patientData, state.selectedDietitian, validatePatientData]);

  // Action functions
  const setCurrentStep = useCallback((step: number) => {
    if (step >= 1 && step <= 3) {
      setState(prev => ({ ...prev, currentStep: step as 1 | 2 | 3 }));
    }
  }, []);

  const updatePatientData = useCallback((data: Partial<PatientData>) => {
    setState(prev => ({
      ...prev,
      patientData: { ...prev.patientData, ...data },
      errors: {}, // Clear errors when data is updated
    }));
  }, []);

  const selectDietitian = useCallback((dietitian: DietitianProfile) => {
    setState(prev => ({
      ...prev,
      selectedDietitian: dietitian,
      errors: { ...prev.errors, dietitianSelection: undefined },
    }));
  }, []);

  const updateFilters = useCallback((filters: Partial<FilterState>) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters },
    }));
  }, []);

  const submitReferral = useCallback(async () => {
    setState(prev => ({ ...prev, isSubmitting: true, errors: {} }));

    try {
      // Validate all data before submission
      const patientErrors = validatePatientData(state.patientData);
      logger.debug('Patient validation errors:', patientErrors);
      if (Object.keys(patientErrors).length > 0 || !state.selectedDietitian) {
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          errors: {
            ...patientErrors,
            ...(state.selectedDietitian ? {} : { dietitianSelection: 'Please select a dietitian' }),
          },
        }));
        return;
      }

      // Submit referral using API service
      const submissionResult = await referralDomain.submitReferral(
        state.patientData,
        state.selectedDietitian
      );

      setState(prev => ({
        ...prev,
        isSubmitting: false,
        submissionResult,
      }));

      logger.debug('submissionResult: ', submissionResult);

      // Upload documents and clear storage after successful submission
      if (submissionResult.status === 'success') {
        logger.debug('state.patientData.documents.length: ', state.patientData.documents?.length);
        // Upload documents if they exist
        if (state.patientData.documents && state.patientData.documents.length > 0) {
          try {
            // Upload each document
            for (const docData of state.patientData.documents) {
              // Convert base64 back to File object for upload
              const byteCharacters = atob(docData.fileData);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const file = new File([byteArray], docData.filename, { type: docData.mimeType });

              logger.log('Upload the document...');

              // Upload the document
              await uploadDocument(submissionResult.referralId, file, {
                description: docData.description,
              });
            }
          } catch (documentError) {
            logger.error('Error uploading documents:', documentError);
            // Note: We don't fail the entire submission if document upload fails
            // The referral was successful, document upload is supplementary
          }
        }

        clearStorage();
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSubmitting: false,
        submissionResult: {
          referralId: '',
          status: 'error',
          message: error instanceof Error ? error.message : 'An error occurred during submission',
          submissionDate: new Date().toISOString(),
        },
      }));
    }
  }, [state.patientData, state.selectedDietitian, validatePatientData, clearStorage]);

  const resetWorkflow = useCallback(() => {
    setState({
      currentStep: 1,
      patientData: {},
      selectedDietitian: null,
      availableDietitians: [],
      filters: {
        search: '',
        specialty: '',
        location: '',
      },
      isLoading: false,
      errors: {},
      isSubmitting: false,
      submissionResult: null,
    });
    clearStorage();
  }, [clearStorage]);

  // Memoized return object
  const returnValue = useMemo(() => ({
    state,
    actions: {
      setCurrentStep,
      updatePatientData,
      selectDietitian,
      updateFilters,
      submitReferral,
      resetWorkflow,
    },
    validation: {
      validateStep,
      getStepErrors,
    },
  }), [
    state,
    setCurrentStep,
    updatePatientData,
    selectDietitian,
    updateFilters,
    submitReferral,
    resetWorkflow,
    validateStep,
    getStepErrors,
  ]);

  return returnValue;
};